import { useCallback, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import { authService } from '../../services/authService';
import { queryKeys } from '../../lib/queryKeys';
import {
  WalkbookStatus,
  type WalkbookDetailResponse,
  type WalkbookListItem,
  type WalkbookResponse,
  type HouseDetailResponse,
  type PolygonPreviewResponse,
  type PolygonUpdateResponse,
  type AssignmentsResponse,
} from '@signalvote/shared';
import { useCampaignStore } from '@/stores';

/**
 * Fetch all active walkbooks (for shift creation dropdown)
 */
export function useWalkbooks() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.walkbooks.list({ status: WalkbookStatus.Active }),
    queryFn: async (): Promise<WalkbookListItem[]> => {
      const api = getApiClient();
      const response = await api.walkbooks.listAll({ status: WalkbookStatus.Active });
      return response.items;
    },
    enabled: !!selectedCampaign && authService.isAuthenticated(),
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch all active walkbooks WITH polygon data for map rendering.
 * Uses includePolygon=true to get polygon strings.
 */
export function useWalkbooksWithPolygons() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.walkbooks.list({ status: WalkbookStatus.Active, includePolygon: 'true' }),
    queryFn: async (): Promise<WalkbookListItem[]> => {
      const api = getApiClient();
      const response = await api.walkbooks.listAll({
        status: WalkbookStatus.Active,
        includePolygon: true,
      });
      return response.items;
    },
    enabled: !!selectedCampaign && authService.isAuthenticated(),
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch a single walkbook with full detail (houses, assignments, polygon)
 */
export function useWalkbookDetail(walkbookId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.walkbooks.detail(walkbookId ?? ''),
    queryFn: async (): Promise<WalkbookDetailResponse> => {
      const api = getApiClient();
      return api.walkbooks.getWalkbook(walkbookId!);
    },
    enabled: !!walkbookId && authService.isAuthenticated(),
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch houses for a walkbook
 */
export function useWalkbookHouses(walkbookId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.walkbooks.houses(walkbookId ?? undefined),
    queryFn: async (): Promise<HouseDetailResponse[]> => {
      const api = getApiClient();
      const response = await api.walkbooks.getHouses(walkbookId!, { pageSize: 1000 });
      return response.items;
    },
    enabled: !!walkbookId && authService.isAuthenticated(),
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Create a new walkbook from a polygon
 */
export function useCreateWalkbook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; polygon: string }): Promise<WalkbookResponse> => {
      const api = getApiClient();
      return api.walkbooks.createWalkbook(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.all });
    },
  });
}

/**
 * Archive (soft-delete) a walkbook
 */
export function useArchiveWalkbook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const api = getApiClient();
      return api.walkbooks.archiveWalkbook(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.all });
    },
  });
}

/**
 * Assign volunteers to a walkbook
 */
export function useAssignVolunteers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, volunteerIds }: { id: string; volunteerIds: string[] }): Promise<AssignmentsResponse> => {
      const api = getApiClient();
      return api.walkbooks.assignVolunteers(id, { volunteerIds });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.detail(variables.id) });
    },
  });
}

/**
 * Remove a volunteer assignment from a walkbook
 */
export function useRemoveAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ walkbookId, volunteerId }: { walkbookId: string; volunteerId: string }): Promise<void> => {
      const api = getApiClient();
      return api.walkbooks.removeAssignment(walkbookId, volunteerId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.detail(variables.walkbookId) });
    },
  });
}

/**
 * Update walkbook fields (name today; polygon goes through useUpdatePolygon
 * because that endpoint also recomputes houses/voters and returns a different
 * shape).
 */
export function useUpdateWalkbook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const api = getApiClient();
      return api.walkbooks.updateWalkbook(id, { name, polygon: null });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.detail(variables.id) });
    },
  });
}

/**
 * Update the polygon boundary of a walkbook
 */
export function useUpdatePolygon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, polygon }: { id: string; polygon: string }): Promise<PolygonUpdateResponse> => {
      const api = getApiClient();
      return api.walkbooks.updatePolygon(id, { polygon });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.detail(variables.id) });
    },
  });
}

/**
 * Preview what a polygon would contain (houses/voters) before creating/saving.
 *
 * Uses a sequence guard so rapid calls (e.g. during polygon vertex drags that
 * emit many `draw.update` events) can't produce stale data: if an earlier
 * request resolves after a later one, its response is discarded. Without this,
 * the Save button (gated on `houseCount > 0`) could get stuck disabled even
 * though the current polygon contains houses.
 */
export function usePreviewPolygon() {
  const seqRef = useRef(0);
  const [data, setData] = useState<PolygonPreviewResponse | undefined>(undefined);
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback((polygon: string) => {
    const mySeq = ++seqRef.current;
    setIsPending(true);
    const api = getApiClient();
    api.walkbooks
      .previewPolygon({ polygon })
      .then((result) => {
        if (mySeq !== seqRef.current) return;
        setData(result);
        setIsPending(false);
      })
      .catch(() => {
        if (mySeq !== seqRef.current) return;
        setIsPending(false);
      });
  }, []);

  const reset = useCallback(() => {
    seqRef.current++;
    setData(undefined);
    setIsPending(false);
  }, []);

  return { mutate, reset, data, isPending };
}
